import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
  styled,
} from "@mui/material";
import { ReactComponent as LeftArrowIcon } from "../../../../assets/svg/LeftArrowIcon.svg";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Setting } from "../../../../utils/Setting";
import { toast } from "react-toastify";
import { getApiData } from "../../../../utils/APIHelper";
import { SCOPE_CR_STATUS, SCOPE_CR_STATUS_DATA } from "../../../../config/constants";
import moment from "moment";
import { formattedAmountWithoutAed } from "../../../../utils/CommonFunction";
import { ReactComponent as DhiramIcon } from "../../../../assets/svg/DhiramIcon.svg";
import { renderCurrencyIcon } from "../../../../utils/CurrencyUtils";
import { getStatusDisplayText } from "./ChangeRequestList";

const StyledTableCell = styled(TableCell)(() => ({
  borderBottom: "1px solid #F1F2F4",
  textTransform: "capitalize",
  fontSize: 13,
  color: "#0D0D0E",
  padding: "12px 16px",
}));

// Shared table styling: soft gray header, subtle row hover, no last-row border.
const tableSx = {
  "& thead th": {
    color: "#949DAA",
    fontWeight: 500,
    fontSize: 12,
    textTransform: "none",
    background: "#FAFAFB",
    borderBottom: "1px solid #ECECEE",
  },
  "& tbody tr:last-of-type td": { borderBottom: "none" },
  "& tbody tr:hover": { background: "#F9FAFB" },
};

const StyledTab = styled(Tab)(({ selected }) => ({
  color: selected ? "#fff !important" : "#949DAA",
  backgroundColor: selected ? "#0D0D0E !important" : "#fff !important",
  border: "1px solid #D7D8D9",
  borderRadius: "10px",
  minHeight: "40px !important",
  fontWeight: 500,
  textTransform: "none",
  marginRight: "10px",
  padding: "4px 16px !important",
  "&:hover": {
    backgroundColor: selected ? "#0D0D0E" : "#F7F7F7",
    color: !selected ? "#000" : undefined,
  },
}));

const EmptyState = ({ label }) => (
  <Box
    sx={{
      p: 4,
      textAlign: "center",
      border: "1px dashed #D7D8D9",
      borderRadius: 2,
      background: "#FAFAFA",
    }}
  >
    <Typography sx={{ fontSize: 13, color: "#949DAA" }}>{label}</Typography>
  </Box>
);

const SectionHeader = ({ title, count }) => (
  <Stack direction="row" alignItems="center" gap={1} mb={1}>
    <Typography sx={{ fontWeight: 600, color: "#0D0D0E" }}>{title}</Typography>
    {typeof count === "number" && (
      <Chip size="small" label={count} sx={{ background: "#F1F2F4" }} />
    )}
  </Stack>
);

const KVP = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: 11, color: "#949DAA" }}>{label}</Typography>
    <Typography sx={{ fontSize: 14, color: "#0D0D0E", fontWeight: 500 }}>
      {value ?? "-"}
    </Typography>
  </Box>
);

const StatusPill = ({ status }) => {
  const meta = SCOPE_CR_STATUS_DATA[status];
  return (
    <Stack direction="row" alignItems="center" gap={0.5}>
      <Box
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          background: meta?.color || "#949DAA",
          border: "3px solid #E5E5E5",
        }}
      />
      <Typography sx={{ fontSize: 14, color: "#0D0D0E" }}>
        {meta?.label || getStatusDisplayText(status)}
      </Typography>
    </Stack>
  );
};

const DrawingCard = ({ drawing, label }) => {
  if (!drawing) {
    return (
      <Box
        sx={{
          p: 2,
          border: "1px dashed #D7D8D9",
          borderRadius: 2,
          background: "#FAFAFA",
          textAlign: "center",
          minHeight: 180,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography sx={{ fontSize: 11, color: "#949DAA", mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: 13, color: "#B0B3B8" }}>Not uploaded yet</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ p: 1.5, border: "1px solid #D7D8D9", borderRadius: 2, background: "#fff" }}>
      <Typography sx={{ fontSize: 11, color: "#949DAA", mb: 0.5 }}>{label}</Typography>
      <Box
        sx={{
          aspectRatio: "16/9",
          borderRadius: 1.5,
          overflow: "hidden",
          background: "#F1F2F4",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 1,
        }}
      >
        {drawing.image_url ? (
          <img
            src={drawing.image_url}
            alt={drawing.type || "drawing"}
            style={{ width: "100%", height: "100%", objectFit: "contain" }}
            onError={(e) => { e.target.style.display = "none"; }}
          />
        ) : (
          <Typography sx={{ color: "#B0B3B8" }}>No preview</Typography>
        )}
      </Box>
      <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{drawing.type || "Drawing"}</Typography>
        <Chip size="small" label={`v${drawing.version ?? "-"}`} />
        {drawing.status && <Chip size="small" label={drawing.status} sx={{ background: "#0D0D0E", color: "#fff" }} />}
        {drawing.image_url && (
          <a
            href={drawing.image_url}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 12, color: "#4D6BF6", marginLeft: "auto" }}
          >
            Open file
          </a>
        )}
      </Stack>
    </Box>
  );
};

const DesignItemsSection = ({ items }) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <Stack gap={2}>
      <SectionHeader title="Design items" count={items.length} />
      {items.map((item) => (
        <Box
          key={item.id}
          sx={{ p: 2, border: "1px solid #D7D8D9", borderRadius: 2, background: "#fff" }}
        >
          <Stack direction="row" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {item.original_drawing?.type || `Drawing #${item.original_drawing_id}`}
            </Typography>
            <StatusPill status={item.status} />
            {item.reason && (
              <Typography sx={{ fontSize: 12, color: "#949DAA", ml: "auto" }}>
                {item.reason}
              </Typography>
            )}
          </Stack>
          <Stack direction="row" gap={2}>
            <Box flex={1}>
              <DrawingCard drawing={item.original_drawing} label="Original" />
            </Box>
            <Box flex={1}>
              <DrawingCard drawing={item.new_drawing} label="Replacement" />
            </Box>
          </Stack>
          {Array.isArray(item.related_milestones) && item.related_milestones.length > 0 && (
            <Stack direction="row" gap={1} flexWrap="wrap" mt={1.5} pt={1.5} sx={{ borderTop: "1px solid #F1F2F4" }}>
              <Typography sx={{ fontSize: 11, color: "#949DAA" }}>Affects activities:</Typography>
              {item.related_milestones.map((m) => (
                <Chip
                  key={m.id}
                  size="small"
                  label={m.milestone_name || m.name || `Activity #${m.id}`}
                  sx={{ background: "#EAF0FF", color: "#4D6BF6" }}
                />
              ))}
            </Stack>
          )}
          {item.rejection_reason && (
            <Box sx={{ mt: 1.5, p: 1, border: "1px solid #FCD5D5", background: "#FFF5F5", borderRadius: 1 }}>
              <Typography sx={{ fontSize: 12, color: "#B42318" }}>
                Rejection reason: {item.rejection_reason}
              </Typography>
            </Box>
          )}
        </Box>
      ))}
    </Stack>
  );
};

const BOQ_STATUS_STYLE = {
  added: { label: "Added", color: "#1B873F", bg: "#E7F5EC" },
  deleted: { label: "Deleted", color: "#D14343", bg: "#FBEAEA" },
  edited: { label: "Edited", color: "#B76E00", bg: "#FBF3E6" },
  unchanged: { label: "Unchanged", color: "#5B6472", bg: "#F1F2F4" },
};

const renderBoqStatus = (status) => {
  if (!status) return "-";
  const s = BOQ_STATUS_STYLE[status] || {
    label: status,
    color: "#5B6472",
    bg: "#F1F2F4",
  };
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        px: 1,
        py: 0.25,
        borderRadius: 1,
        fontSize: 11,
        fontWeight: 600,
        color: s.color,
        background: s.bg,
      }}
    >
      {s.label}
    </Box>
  );
};

const fmtDate = (d) => (d ? moment(d).format("MMM DD, YYYY") : "-");

// Shows a date; if it differs from its original value, highlights the new date
// and shows the old one struck through underneath.
const DateCell = ({ current, original }) => {
  const changed =
    original != null &&
    current != null &&
    !moment(original).isSame(moment(current), "day");
  if (!changed) {
    return <Typography sx={{ fontSize: 13 }}>{fmtDate(current)}</Typography>;
  }
  return (
    <Stack gap={0.25}>
      <Box
        component="span"
        sx={{
          display: "inline-block",
          alignSelf: "flex-start",
          px: 0.75,
          borderRadius: 1,
          fontSize: 13,
          fontWeight: 600,
          color: "#B76E00",
          background: "#FBF3E6",
        }}
      >
        {fmtDate(current)}
      </Box>
      <Typography
        sx={{ fontSize: 11, color: "#949DAA", textDecoration: "line-through" }}
      >
        {fmtDate(original)}
      </Typography>
    </Stack>
  );
};

const ActivitiesSection = ({ milestones }) => {
  if (!milestones?.length) return <EmptyState label="No activity changes in this request." />;
  const hasDateChange = (m) =>
    (m.original_start_date != null &&
      m.start_date != null &&
      !moment(m.original_start_date).isSame(moment(m.start_date), "day")) ||
    (m.original_end_date != null &&
      m.end_date != null &&
      !moment(m.original_end_date).isSame(moment(m.end_date), "day"));
  return (
    <Stack gap={1}>
      <SectionHeader title="Activities" count={milestones.length} />
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #ECECEE", borderRadius: 2, boxShadow: "none", overflowX: "auto" }}>
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Activity</StyledTableCell>
              <StyledTableCell>Start</StyledTableCell>
              <StyledTableCell>End</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {milestones.map((m) => (
              <TableRow
                key={m.id}
                sx={{
                  opacity: m.status === "deleted" ? 0.55 : 1,
                  background: hasDateChange(m) ? "#FFFBF2" : "transparent",
                }}
              >
                <StyledTableCell>{m.milestone_name || m.name || `#${m.id}`}</StyledTableCell>
                <StyledTableCell>
                  <DateCell current={m.start_date} original={m.original_start_date} />
                </StyledTableCell>
                <StyledTableCell>
                  <DateCell current={m.end_date} original={m.original_end_date} />
                </StyledTableCell>
                <StyledTableCell>{renderBoqStatus(m.status)}</StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

const BoqTotals = ({ boqTotals }) => {
  if (!boqTotals || boqTotals.old == null || boqTotals.draft == null) return null;
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Typography sx={{ fontSize: 13, color: "#949DAA" }}>
        Old total {formattedAmountWithoutAed(boqTotals.old)}
      </Typography>
      <Typography sx={{ fontSize: 13, color: "#949DAA" }}>→</Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
        New total {formattedAmountWithoutAed(boqTotals.draft)}
      </Typography>
      {boqTotals.delta != null && boqTotals.delta !== 0 && (
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: boqTotals.delta > 0 ? "#1B873F" : "#D14343",
          }}
        >
          ({boqTotals.delta > 0 ? "+" : ""}
          {formattedAmountWithoutAed(boqTotals.delta)})
        </Typography>
      )}
    </Stack>
  );
};

// Contractor portal shows the CONTRACTOR COST (project cost = what we pay the
// contractor), NOT the customer-facing price (project value). Mirrors the
// backend COALESCE(total_item, qty * material_unit_price) cost basis so the
// per-line values reconcile with the cost-based Old/New totals.
const boqCost = (b) => {
  if (!b) return null;
  if (b.total_item != null) return Number(b.total_item);
  if (b.qty != null && b.material_unit_price != null) {
    return Number(b.qty) * Number(b.material_unit_price);
  }
  return null;
};

const BoqSection = ({ boqs, boqTotals }) => {
  if (!boqs?.length) return <EmptyState label="No BOQ changes in this request." />;
  return (
    <Stack gap={1}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <SectionHeader title="BOQ" count={boqs.length} />
        <BoqTotals boqTotals={boqTotals} />
      </Stack>
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #ECECEE", borderRadius: 2, boxShadow: "none", overflowX: "auto" }}>
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Item</StyledTableCell>
              <StyledTableCell>Type</StyledTableCell>
              <StyledTableCell align="right">Qty</StyledTableCell>
              <StyledTableCell align="right">Cost ({renderCurrencyIcon("small")})</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {boqs.map((b) => (
              <TableRow key={b.id} sx={{ opacity: b.status === "deleted" ? 0.55 : 1 }}>
                <StyledTableCell>{b.name || b.description || `#${b.id}`}</StyledTableCell>
                <StyledTableCell>{b.boq_type || b.type || "-"}</StyledTableCell>
                <StyledTableCell align="right">
                  {(b.boq_type || b.type) === "Materials"
                    ? b.qty != null
                      ? `${b.qty}${b.material_unit ? ` ${b.material_unit}` : ""}`
                      : "-"
                    : b.days != null
                      ? `${b.days} days`
                      : "-"}
                </StyledTableCell>
                <StyledTableCell align="right">
                  {boqCost(b) != null ? formattedAmountWithoutAed(boqCost(b)) : "-"}
                </StyledTableCell>
                <StyledTableCell>{renderBoqStatus(b.status)}</StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

const TimelineSection = ({ timelineImpact }) => {
  if (!timelineImpact?.length) return <EmptyState label="No timeline impact in this request." />;
  return (
    <Stack gap={1}>
      <SectionHeader title="Timeline impact" />
      {timelineImpact.map((t, idx) => (
        <Box key={t.id ?? idx} sx={{ p: 1.5, border: "1px solid #D7D8D9", borderRadius: 2 }}>
          <Stack direction="row" gap={3} flexWrap="wrap">
            <KVP
              label="Planned start"
              value={t.planned_start_date ? moment(t.planned_start_date).format("MMM DD, YYYY") : "-"}
            />
            <KVP
              label="Planned end"
              value={t.planned_end_date ? moment(t.planned_end_date).format("MMM DD, YYYY") : "-"}
            />
            <KVP label="Span (days)" value={t.draft_span_days ?? "-"} />
            <KVP label="Net change (days)" value={t.net_change_days ?? "-"} />
          </Stack>
          {t.notes && (
            <Typography sx={{ fontSize: 12, color: "#949DAA", mt: 1 }}>{t.notes}</Typography>
          )}
        </Box>
      ))}
    </Stack>
  );
};

const StatCard = ({ label, children }) => (
  <Box
    sx={{
      flex: "1 1 220px",
      minWidth: 200,
      p: 2.5,
      border: "1px solid #ECECEE",
      borderRadius: 2.5,
      background: "#fff",
      display: "flex",
      flexDirection: "column",
      gap: 1.25,
    }}
  >
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: "#949DAA",
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

const StatValue = ({ children }) => (
  <Typography sx={{ fontSize: 22, fontWeight: 700, lineHeight: 1.1, color: "#0D0D0E" }}>
    {children}
  </Typography>
);

const DeltaChip = ({ value }) => {
  if (value == null || value === 0) return null;
  return (
    <Typography
      component="span"
      sx={{ fontSize: 13, fontWeight: 600, color: value > 0 ? "#1B873F" : "#D14343" }}
    >
      {value > 0 ? "+" : ""}
      {formattedAmountWithoutAed(value)}
    </Typography>
  );
};

const ChangeSummary = ({ snapshot, payoutsData }) => {
  const boqs = Array.isArray(snapshot?.boqs) ? snapshot.boqs : [];
  const timeline = Array.isArray(snapshot?.timeline_impact) ? snapshot.timeline_impact : [];
  const t0 = timeline[0];
  const counts = boqs.reduce((acc, b) => {
    const key = b.status || "unchanged";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const changedKeys = ["added", "edited", "deleted"];
  const hasChanges = changedKeys.some((k) => counts[k]);

  return (
    <Stack gap={1.5}>
      <SectionHeader title="Change summary" />
      <Stack direction="row" gap={2} flexWrap="wrap">
        <StatCard label="BOQ total">
          <StatValue>
            {payoutsData?.draft_boq_total != null
              ? formattedAmountWithoutAed(payoutsData.draft_boq_total)
              : "-"}
          </StatValue>
          <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
            <Typography sx={{ fontSize: 12, color: "#949DAA" }}>
              from{" "}
              {payoutsData?.old_boq_total != null
                ? formattedAmountWithoutAed(payoutsData.old_boq_total)
                : "-"}
            </Typography>
            <DeltaChip value={payoutsData?.boq_delta} />
          </Stack>
          <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center">
            {hasChanges ? (
              changedKeys
                .filter((k) => counts[k])
                .map((k) => (
                  <Stack key={k} direction="row" alignItems="center" gap={0.5}>
                    {renderBoqStatus(k)}
                    <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#5B6472" }}>
                      {counts[k]}
                    </Typography>
                  </Stack>
                ))
            ) : (
              <Typography sx={{ fontSize: 12, color: "#949DAA" }}>No line changes</Typography>
            )}
          </Stack>
        </StatCard>

        <StatCard label="Contractor payout">
          <Stack direction="row" alignItems="center" gap={0.5}>
            <DhiramIcon height={18} />
            <StatValue>
              {payoutsData?.totalAmount != null
                ? formattedAmountWithoutAed(payoutsData.totalAmount)
                : "-"}
            </StatValue>
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.5}>
            <Typography sx={{ fontSize: 12, color: "#949DAA" }}>Change</Typography>
            {payoutsData?.contractor_delta ? (
              <DeltaChip value={payoutsData.contractor_delta} />
            ) : (
              <Typography sx={{ fontSize: 12, color: "#949DAA" }}>—</Typography>
            )}
          </Stack>
        </StatCard>

        <StatCard label="Timeline">
          <StatValue>
            {t0?.draft_span_days != null ? `${t0.draft_span_days} days` : "-"}
          </StatValue>
          <Typography sx={{ fontSize: 12, color: "#949DAA" }}>
            Net change {t0?.net_change_days != null ? `${t0.net_change_days} days` : "—"}
          </Typography>
        </StatCard>
      </Stack>
    </Stack>
  );
};

const PayoutsSection = ({ data }) => {
  const payouts = Array.isArray(data?.payouts) ? data.payouts : [];
  if (payouts.length === 0) return null;
  const totalAmount = data?.totalAmount;
  const contractorName = data?.contractor?.companyName;
  const contractorDelta = data?.contractor_delta;
  return (
    <Stack gap={1}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={1}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <SectionHeader title="Payouts" count={payouts.length} />
          {contractorName && (
            <Typography sx={{ fontSize: 12, color: "#949DAA" }}>· {contractorName}</Typography>
          )}
        </Stack>
        <Stack direction="row" alignItems="center" gap={2}>
          {contractorDelta != null && contractorDelta !== 0 && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography sx={{ fontSize: 13, color: "#949DAA" }}>Contractor change:</Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  color: contractorDelta > 0 ? "#1B873F" : "#D14343",
                }}
              >
                {contractorDelta > 0 ? "+" : ""}
                {formattedAmountWithoutAed(contractorDelta)}
              </Typography>
            </Stack>
          )}
          {totalAmount != null && (
            <Stack direction="row" alignItems="center" gap={0.5}>
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>Total:</Typography>
              <DhiramIcon height={12} />
              <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                {formattedAmountWithoutAed(totalAmount)}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #ECECEE", borderRadius: 2, boxShadow: "none", overflowX: "auto" }}>
        <Table size="small" sx={tableSx}>
          <TableHead>
            <TableRow>
              <StyledTableCell>Payout</StyledTableCell>
              <StyledTableCell align="right">
                Amount ({renderCurrencyIcon("small")})
              </StyledTableCell>
              <StyledTableCell align="right">
                Projected ({renderCurrencyIcon("small")})
              </StyledTableCell>
              <StyledTableCell align="right">Change</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {payouts.map((p, idx) => {
              const projected = p.projectedAmount != null ? p.projectedAmount : p.amount;
              const delta = p.crDelta;
              return (
                <TableRow key={`payout-${p.payoutId ?? p.referenceId ?? idx}`}>
                  <StyledTableCell>
                    {p.description || "-"}
                    {p.isAdjustment && (
                      <Typography
                        component="span"
                        sx={{ fontSize: 11, color: "#B76E00", ml: 0.75 }}
                      >
                        (adjustment)
                      </Typography>
                    )}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {p.amount != null ? formattedAmountWithoutAed(p.amount) : "-"}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {projected != null ? formattedAmountWithoutAed(projected) : "-"}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {delta == null || delta === 0 ? (
                      "-"
                    ) : (
                      <Typography
                        component="span"
                        sx={{ fontSize: 13, color: delta > 0 ? "#1B873F" : "#D14343" }}
                      >
                        {delta > 0 ? "+" : ""}
                        {formattedAmountWithoutAed(delta)}
                      </Typography>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    {p.status ? getStatusDisplayText(p.status) : "-"}
                  </StyledTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  );
};

const CommentsSection = ({ comments }) => {
  // Filter out internal JSON-blob comments (the snapshot stash).
  const visible = (Array.isArray(comments) ? comments : []).filter((c) => {
    const text = String(c?.comment || "").trim();
    if (!text || text.startsWith("{")) return false;
    return !c?.is_internal;
  });
  if (!visible.length) return null;
  return (
    <Stack gap={1}>
      <SectionHeader title="Comments" count={visible.length} />
      <Stack gap={0.5} sx={{ border: "1px solid #D7D8D9", borderRadius: 2 }}>
        {visible.map((c) => (
          <Box key={c.id} sx={{ p: 1.5, borderBottom: "1px solid #F1F2F4" }}>
            <Typography sx={{ fontSize: 13 }}>{c.comment}</Typography>
            <Typography sx={{ fontSize: 11, color: "#949DAA", mt: 0.5 }}>
              {c.created_at ? moment(c.created_at).format("MMM DD, YYYY HH:mm") : ""}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
};

const ChangeRequestOverview = ({ projectDetails }) => {
  const [searchQuery] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [cr, setCr] = useState(null);
  const [payoutsData, setPayoutsData] = useState(null);
  const [acting, setActing] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const changeId = searchQuery.get("change");

  const fetchDetails = async () => {
    if (!changeId) return;
    try {
      setLoading(true);
      const [detail, payouts] = await Promise.all([
        getApiData(Setting.endpoints.changeRequest.details(changeId), "GET", {}, {}, false),
        getApiData(
          Setting.endpoints.changeRequest.contractorPayouts(changeId),
          "GET",
          {},
          {},
          false
        ).catch(() => null),
      ]);
      // detail is { success, data: {...cr, oldScope, draftScope, design_items, status_logs, comments, draft_snapshot } }
      // or legacy shape; normalize.
      setCr(detail?.data ?? detail);
      // payouts is { success, data: { payouts, totalAmount, old_boq_total, draft_boq_total, boq_delta, contractor, ... } }
      setPayoutsData(payouts?.data ?? payouts);
    } catch (err) {
      toast.error(err?.message || "Failed to load change request.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeId]);

  const handleApprove = async () => {
    setActing(true);
    try {
      await getApiData(
        Setting.endpoints.changeRequest.approve(changeId),
        "POST",
        { comment: "Approved by contractor" },
        { "Content-Type": "application/json" },
        false
      );
      toast.success("Change request approved.");
      fetchDetails();
    } catch (err) {
      toast.error(err?.message || "Failed to approve.");
    } finally {
      setActing(false);
    }
  };

  const handleReject = () => {
    setRejectReason("");
    setRejectOpen(true);
  };

  const confirmReject = async () => {
    const reason = rejectReason.trim();
    if (!reason) return;
    setActing(true);
    try {
      await getApiData(
        Setting.endpoints.changeRequest.reject(changeId),
        "POST",
        { comment: reason, rejection_reason: reason },
        { "Content-Type": "application/json" },
        false
      );
      toast.success("Change request rejected.");
      setRejectOpen(false);
      setRejectReason("");
      fetchDetails();
    } catch (err) {
      toast.error(err?.message || "Failed to reject.");
    } finally {
      setActing(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!cr) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Change request not found.</Typography>
      </Box>
    );
  }

  const isDesign = cr.change_type === "DESIGN_UPDATE";
  const designItems = Array.isArray(cr.design_items) ? cr.design_items : [];
  const canAct = cr.status === SCOPE_CR_STATUS.SUBMITTED;

  const snapshot = cr.draft_snapshot;
  const milestones = Array.isArray(snapshot?.milestones) ? snapshot.milestones : [];
  const boqs = Array.isArray(snapshot?.boqs) ? snapshot.boqs : [];
  const timelineImpact = Array.isArray(snapshot?.timeline_impact)
    ? snapshot.timeline_impact
    : [];
  const boqTotals = {
    old: payoutsData?.old_boq_total,
    draft: payoutsData?.draft_boq_total,
    delta: payoutsData?.boq_delta,
  };
  const SCOPE_TABS = ["Overview", "BOQ", "Payouts", "Timeline"];

  const MetaBand = (
    <Stack
      direction="row"
      gap={4}
      flexWrap="wrap"
      sx={{ p: 2, background: "#FAFAFA", borderRadius: 2 }}
    >
      <KVP
        label="Created"
        value={cr.created_at ? moment(cr.created_at).format("MMM DD, YYYY HH:mm") : "-"}
      />
      <KVP
        label="Submitted"
        value={cr.submitted_at ? moment(cr.submitted_at).format("MMM DD, YYYY HH:mm") : "-"}
      />
      <KVP
        label="Contractor approved"
        value={
          cr.contractor_approved_at
            ? moment(cr.contractor_approved_at).format("MMM DD, YYYY HH:mm")
            : "-"
        }
      />
      <KVP
        label="Homeowner approved"
        value={cr.approved_at ? moment(cr.approved_at).format("MMM DD, YYYY HH:mm") : "-"}
      />
      {cr.rejected_at && (
        <KVP label="Rejected" value={moment(cr.rejected_at).format("MMM DD, YYYY HH:mm")} />
      )}
    </Stack>
  );

  const ReasonBlocks = (
    <>
      {cr.reason && (
        <Box sx={{ p: 2, background: "#FAFAFA", borderRadius: 2 }}>
          <Typography sx={{ fontSize: 11, color: "#949DAA" }}>Reason</Typography>
          <Typography sx={{ fontSize: 14, mt: 0.5 }}>{cr.reason}</Typography>
        </Box>
      )}
      {cr.rejection_reason && (
        <Box sx={{ p: 2, background: "#FFF5F5", border: "1px solid #FCD5D5", borderRadius: 2 }}>
          <Typography sx={{ fontSize: 11, color: "#B42318" }}>Rejection reason</Typography>
          <Typography sx={{ fontSize: 14, mt: 0.5, color: "#B42318" }}>
            {cr.rejection_reason}
          </Typography>
        </Box>
      )}
    </>
  );

  return (
    <Stack gap={3} sx={{ pr: { xs: 1.5, md: 3 } }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <LeftArrowIcon
            onClick={() => navigate(`/project/details/${projectId}/change-request`)}
            style={{ cursor: "pointer" }}
          />
          <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
            {isDesign
              ? `Design Change Request #${cr.id}`
              : `Change Request #${cr.id}`}
          </Typography>
          <Chip
            size="small"
            label={getStatusDisplayText(cr.change_type || "SCOPE")}
            sx={{
              background: isDesign ? "#7c3aed" : "#0D0D0E",
              color: "#fff",
            }}
          />
          <StatusPill status={cr.status} />
        </Stack>
        {canAct && (
          <Stack direction="row" gap={1}>
            <Button
              onClick={handleApprove}
              variant="contained"
              color="primary"
              disabled={acting}
              sx={{ textTransform: "none" }}
            >
              Approve
            </Button>
            <Button
              onClick={handleReject}
              variant="outlined"
              disabled={acting}
              sx={{ textTransform: "none" }}
            >
              Reject
            </Button>
          </Stack>
        )}
      </Stack>

      <Dialog
        open={rejectOpen}
        onClose={() => !acting && setRejectOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Reject change request</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: "#667085", mb: 1.5 }}>
            Let the admin know why you're rejecting this change request.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            placeholder="Reason for rejection..."
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            disabled={acting}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRejectOpen(false)}
            disabled={acting}
            sx={{ textTransform: "none", color: "#667085" }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmReject}
            variant="contained"
            color="error"
            disabled={acting || !rejectReason.trim()}
            sx={{ textTransform: "none" }}
          >
            {acting ? "Rejecting..." : "Confirm reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Design CR: single view (no tabs) */}
      {isDesign ? (
        <Stack gap={3}>
          {MetaBand}
          {ReasonBlocks}
          <Divider />
          <DesignItemsSection items={designItems} />
          <CommentsSection comments={cr.comments} />
        </Stack>
      ) : (
        <>
          {/* Tabs */}
          <Tabs
            value={tabValue}
            variant="scrollable"
            scrollButtons="auto"
            onChange={(e, v) => setTabValue(v)}
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{ minHeight: 40, "& .MuiTabs-flexContainer": { gap: 0 } }}
          >
            {SCOPE_TABS.map((title, index) => (
              <StyledTab key={title} label={title} selected={tabValue === index} />
            ))}
          </Tabs>

          <Divider sx={{ mt: -1.5, borderColor: "#ECECEE" }} />

          {/* Overview */}
          {tabValue === 0 && (
            <Stack gap={3}>
              {MetaBand}
              <ChangeSummary snapshot={snapshot} payoutsData={payoutsData} />
              {ReasonBlocks}
              <CommentsSection comments={cr.comments} />
            </Stack>
          )}

          {/* BOQ */}
          {tabValue === 1 && <BoqSection boqs={boqs} boqTotals={boqTotals} />}

          {/* Payouts */}
          {tabValue === 2 &&
            (payoutsData?.payouts?.length ? (
              <PayoutsSection data={payoutsData} />
            ) : (
              <EmptyState label="No contractor payouts for this request." />
            ))}

          {/* Timeline */}
          {tabValue === 3 && (
            <Stack gap={3}>
              <ActivitiesSection milestones={milestones} />
              <TimelineSection timelineImpact={timelineImpact} />
            </Stack>
          )}
        </>
      )}
    </Stack>
  );
};

export default ChangeRequestOverview;
